import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Button, Divider, Flex, Form, InputNumber, Segmented, Select, Space,
    Spin, Statistic, Table, Tag, Typography, message,
} from 'antd';
import {
    LinkOutlined, ReloadOutlined, SaveOutlined, ShoppingCartOutlined,
} from '@ant-design/icons';
import CustomCard from '../../components/Card';
import OneApiService from '../../APIServices/OneApiService';
import { money, perGb, data } from './oneHelpers';

const { Title, Text, Paragraph } = Typography;

/**
 * What YouGuide One sells and for how much.
 *
 * THESE VALUES GOVERN THE APP AND /new-esims TOGETHER. Both read the same
 * endpoint, so a markup change here moves the price on a phone installed last
 * month — there is no second place to edit and nothing to keep in step.
 *
 * The preview below is the point of the page. Markup is an abstract number
 * until you see what a gigabyte of Spain costs a customer afterwards, so the
 * table is built from the SAME endpoint the app calls rather than a
 * re-implementation that could drift.
 */
export default function OneSettings() {
    const [form] = Form.useForm();
    const [settings, setSettings] = useState(null);
    const [preview, setPreview] = useState([]);
    const [mode, setMode] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Watched rather than read on submit, because the rest of this page means
     * something different under each mode and an operator should see that
     * before they save, not after.
     */
    const salesMode = Form.useWatch('salesMode', form) ?? settings?.salesMode ?? 'cart';
    const linkMode = salesMode === 'link';

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [s, d] = await Promise.all([
                OneApiService.getSettings(),
                OneApiService.previewDestinations().catch(() => ({ destinations: [] })),
            ]);
            setSettings(s);
            setPreview(d.destinations);
            setMode(d.mode);
            form.setFieldsValue(s);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [form]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }

        setSaving(true);
        try {
            // The response is what the app will actually be served, after the
            // backend's coercion and sanity checks — not an echo of the input.
            const saved = await OneApiService.updateSettings(values);
            setSettings(saved);
            form.setFieldsValue(saved);
            message.success('Saved. The app and /new-esims pick this up within seconds.');

            const next = await OneApiService
                .previewDestinations()
                .catch(() => ({ destinations: [] }));
            setPreview(next.destinations);
            setMode(next.mode);
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const withChoice = preview.filter((d) => d.hasOperatorChoice);

    // Every figure below is in the STORE currency. The symbol comes from the
    // server rather than being typed here, because the page used to print '$'
    // in front of euro prices.
    const symbol = settings?.currencySymbol || preview[0]?.currencySymbol || '\u20ac';

    const perMb = mode === 'telna';

    /**
     * The two providers sell DIFFERENT PRODUCTS, so the preview cannot have
     * one set of columns.
     *
     * Telna is a per-megabyte rate with an operator ladder. eSIM Access is a
     * shelf of fixed bundles and carries no `retailPerMb` at all — which this
     * table used to read regardless, rendering a confident €0.00 against all
     * 201 destinations.
     */
    const columns = perMb ? [
        { title: '', dataIndex: 'flag', width: 44 },
        { title: 'Destination', dataIndex: 'name' },
        {
            title: 'Customer pays',
            dataIndex: 'retailPerMb',
            align: 'right',
            render: (v) => <Text strong>{perGb(v, symbol)}</Text>,
            sorter: (a, b) => (a.retailPerMb || 0) - (b.retailPerMb || 0),
        },
        {
            title: '100 MB',
            dataIndex: 'retailPerMb',
            align: 'right',
            render: (v) => money(v * 100, symbol),
        },
        {
            title: '1 GB',
            dataIndex: 'retailPerMb',
            align: 'right',
            render: (v) => money(v * 1024, symbol),
        },
        {
            title: 'Operator levels',
            dataIndex: 'hasOperatorChoice',
            align: 'center',
            // Every operator is listed, cheapest first. Levels below the top
            // need a Telna whitelist to restrict the SIM — created when a
            // customer buys that level, not before — so a country with none
            // mapped yet still shows its full ladder.
            render: (v, r) => {
                if (!v) return <Text type="secondary">one operator</Text>;
                const pending = (r.tiers || [])
                    .filter((t) => t.requiresWhitelist && !t.whitelistReady).length;
                return (
                    <Space size={4}>
                        <Tag color="blue">{r.tiers?.length ?? 0} levels</Tag>
                        {pending > 0 && (
                            <Tag color="orange">{pending} to whitelist</Tag>
                        )}
                    </Space>
                );
            },
            filters: [
                { text: 'Ladder offered', value: true },
                { text: 'Single level', value: false },
            ],
            onFilter: (value, r) => Boolean(r.hasOperatorChoice) === value,
        },
    ] : [
        { title: '', dataIndex: 'flag', width: 44 },
        { title: 'Destination', dataIndex: 'name' },
        {
            title: 'From',
            dataIndex: 'fromPrice',
            align: 'right',
            render: (v) => <Text strong>{money(v, symbol)}</Text>,
            sorter: (a, b) => (a.fromPrice || 0) - (b.fromPrice || 0),
        },
        {
            title: 'Cheapest plan',
            dataIndex: 'plans',
            render: (v) => (v?.length
                ? <Text type="secondary">{v[0].label}</Text>
                : <Text type="secondary">—</Text>),
        },
        {
            title: 'Plans',
            dataIndex: 'plans',
            align: 'right',
            render: (v) => (v?.length ?? 0),
            sorter: (a, b) => (a.plans?.length ?? 0) - (b.plans?.length ?? 0),
        },
        {
            title: 'Largest',
            dataIndex: 'plans',
            align: 'right',
            // The top of the shelf, which is what tells you whether a
            // destination can serve a long trip at all.
            render: (v) => (v?.length
                ? data(Math.max(...v.map((p) => p.dataMb)))
                : '—'),
        },
    ];

    /**
     * The headline figures, which are also per-provider.
     *
     * "Offering a network choice" and "Cheapest per GB" are Telna concepts;
     * against fixed bundles they read 0/201 and €0.00, which looks like a
     * broken catalogue rather than a different product.
     */
    const stats = perMb ? [
        { title: 'Destinations on sale', value: preview.length },
        {
            title: 'Offering a network choice',
            value: withChoice.length,
            suffix: `/ ${preview.length}`,
        },
        {
            // The actionable number for Telna: how many whitelists are still
            // to be created before every level can actually be delivered.
            title: 'Whitelists to set up',
            value: preview.reduce(
                (n, d) => n + (d.tiers || [])
                    .filter((t) => t.requiresWhitelist && !t.whitelistReady).length,
                0,
            ),
        },
        {
            title: 'Cheapest per GB',
            // `formatter` rather than `prefix`: antd puts a gap after a
            // prefix, so a price rendered that way reads "€ 0.50".
            value: 0,
            formatter: () => perGb(preview.length
                ? Math.min(...preview.map((d) => d.retailPerMb || Infinity))
                : 0, symbol),
        },
    ] : [
        { title: 'Destinations on sale', value: preview.length },
        {
            title: 'Plans on the shelf',
            value: preview.reduce((n, d) => n + (d.plans?.length ?? 0), 0),
        },
        {
            title: 'Cheapest plan',
            value: 0,
            formatter: () => money(preview.length
                ? Math.min(...preview.map((d) => d.fromPrice || Infinity))
                : 0, symbol),
        },
    ];

    return (
        <Spin spinning={loading}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Flex justify="space-between" align="center" wrap gap={12}>
                    <div>
                        <Title level={4} style={{ margin: 0 }}>
                            Selling, pricing and limits
                        </Title>
                        <Text type="secondary">
                            Where customers pay, and what they pay. Governs the YouGuide One
                            app and the website&apos;s /new-esims page.
                        </Text>
                    </div>
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={load}>Reload</Button>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={saving}
                            onClick={save}
                        >
                            Save
                        </Button>
                    </Space>
                </Flex>

                {error && <Alert type="error" showIcon message={error} />}

                <Alert
                    type="info"
                    showIcon
                    message="These apply to new purchases only"
                    description={
                        'Every order records the price it was sold at, so changing the markup '
                        + 'never alters an order that has already been placed.'
                    }
                />

                <Form form={form} layout="vertical" disabled={saving}>
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>

                    {/*
                      * CART OR LINK — the one setting on this page that
                      * changes the app rather than a number inside it.
                      *
                      * It is first because everything below is read in its
                      * light: a markup governs nothing while the app is not
                      * the thing taking the money.
                      */}
                    <CustomCard
                        title="How the app sells"
                        extra={
                            <Tag
                                color={linkMode ? 'orange' : 'green'}
                                icon={linkMode ? <LinkOutlined /> : <ShoppingCartOutlined />}
                            >
                                {linkMode ? 'Links to youguide.com' : 'Buys in the app'}
                            </Tag>
                        }
                    >
                        <Form.Item
                            name="salesMode"
                            label="Where a customer pays"
                            tooltip="Takes effect within seconds, on phones already installed. No app release needed."
                            style={{ marginBottom: 12 }}
                        >
                            <Segmented
                                size="large"
                                options={[
                                    {
                                        value: 'cart',
                                        label: 'Cart',
                                        icon: <ShoppingCartOutlined />,
                                    },
                                    {
                                        value: 'link',
                                        label: 'Link',
                                        icon: <LinkOutlined />,
                                    },
                                ]}
                            />
                        </Form.Item>

                        {linkMode ? (
                            <Alert
                                type="warning"
                                showIcon
                                message="Nothing is bought inside the app"
                                description={(
                                    <>
                                        <Paragraph style={{ marginBottom: 8 }}>
                                            Every price in the app becomes a link out to the
                                            website, which takes the payment and delivers —
                                            eSIMs, travel guides and language guides alike.
                                            The basket, the wallet and the in-app checkout
                                            are hidden, and the server refuses a purchase
                                            even from an older installed build.
                                        </Paragraph>
                                        <Paragraph style={{ marginBottom: 8 }}>
                                            Customers keep everything they have already
                                            bought: their library, their orders and their
                                            remaining credit are untouched.
                                        </Paragraph>
                                        {settings?.storeLinks && (
                                            <Space direction="vertical" size={2}>
                                                <Text type="secondary">Customers will be sent to:</Text>
                                                <Text code>{settings.storeLinks.esim}</Text>
                                                <Text code>{settings.storeLinks.travelGuides}</Text>
                                                <Text code>{settings.storeLinks.languageGuides}</Text>
                                            </Space>
                                        )}
                                        <Paragraph type="secondary" style={{ margin: '8px 0 0' }}>
                                            Partner editions have no storefront of their own to
                                            link to — sending their customer to youguide.com
                                            would tell them whose product they are using — so a
                                            partner app shows no way to buy at all while this is
                                            set to Link.
                                        </Paragraph>
                                    </>
                                )}
                            />
                        ) : (
                            <Alert
                                type="success"
                                showIcon
                                message="The app is the shop"
                                description={
                                    'A basket, wallet credit and a card payment inside the '
                                    + 'app. eSIMs are delivered to the phone and guides land '
                                    + 'in the customer\u2019s library — the prices and limits '
                                    + 'below are what they are charged.'
                                }
                            />
                        )}
                    </CustomCard>

                    <CustomCard title="Pricing">
                        {linkMode && (
                            <Alert
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                                message="The app is not charging these while it sells by link"
                                description={
                                    'Nothing is bought in the app, so these govern no app '
                                    + 'price today. They are still what the website\u2019s '
                                    + 'per-megabyte pages read, and they apply to the app '
                                    + 'again the moment you switch back to Cart.'
                                }
                            />
                        )}
                        <Flex gap={24} wrap>
                            <Form.Item
                                name="markup"
                                label="Markup"
                                tooltip="1.6 means cost + 60%. Applied to the supplier cost in USD, which the shop's own pricing function converts to the store currency."
                                rules={[{ required: true, type: 'number', min: 1, max: 20 }]}
                                style={{ minWidth: 190 }}
                            >
                                <InputNumber
                                    step={0.05}
                                    min={1}
                                    max={20}
                                    style={{ width: '100%' }}
                                    addonAfter={
                                        <Text type="secondary">
                                            {settings ? `+${Math.round((form.getFieldValue('markup') ?? settings.markup) * 100 - 100)}%` : ''}
                                        </Text>
                                    }
                                />
                            </Form.Item>

                            <Form.Item
                                name="validityDays"
                                label="Validity (days)"
                                tooltip="Still unconfirmed by Telna. Stamped on every order at the moment of sale."
                                rules={[{ required: true, type: 'number', min: 1 }]}
                                style={{ minWidth: 170 }}
                            >
                                <InputNumber min={1} max={730} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item
                                name="carrierSpread"
                                label="Show network choice above"
                                tooltip="How much dearer the worst network must be than the best before the app offers a choice. 1.5 selects about a tenth of countries; everywhere else the networks cost the same and the screen would be a pointless tap."
                                rules={[{ required: true, type: 'number', min: 1 }]}
                                style={{ minWidth: 220 }}
                            >
                                <InputNumber
                                    step={0.1}
                                    min={1}
                                    max={50}
                                    style={{ width: '100%' }}
                                    addonAfter="×"
                                />
                            </Form.Item>
                        </Flex>

                        <Divider orientation="left" plain>What a customer may buy</Divider>

                        <Flex gap={24} wrap>
                            <Form.Item name="minMb" label="Minimum (MB)" style={{ minWidth: 150 }}>
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item name="maxMb" label="Maximum (MB)" style={{ minWidth: 150 }}>
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item name="stepMb" label="Slider step (MB)" style={{ minWidth: 150 }}>
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item
                                name="presetsMb"
                                label="Quick amounts (MB)"
                                style={{ minWidth: 300, flex: 1 }}
                            >
                                <Select mode="tags" tokenSeparators={[',']} />
                            </Form.Item>
                        </Flex>

                        <Divider orientation="left" plain>Wallet</Divider>

                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="Every top-up amount must clear the card minimum"
                            description={
                                `The payment provider refuses anything under about ${money(0.5, symbol)}, `
                                + 'and most per-megabyte purchases are below that — a hundred '
                                + 'megabytes of Spain is seven cents. Credit is how those are paid '
                                + 'for, so an amount below the minimum would be unbuyable.'
                            }
                        />

                        <Form.Item
                            name="topUps"
                            label={`Credit amounts (${settings?.currency || 'store currency'})`}
                            tooltip="In the store currency, not dollars — the same currency every price in the app is charged in."
                            style={{ maxWidth: 480 }}
                        >
                            <Select mode="tags" tokenSeparators={[',']} />
                        </Form.Item>

                        <Divider orientation="left" plain>Where we sell</Divider>

                        <Form.Item
                            name="countries"
                            label="Countries on sale"
                            tooltip="Leave empty to sell every country we hold a rate for. A partner edition can narrow this further, never widen it."
                        >
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Empty = everywhere we have a rate"
                                options={preview.map((d) => ({
                                    value: d.iso2,
                                    label: `${d.flag} ${d.name}`,
                                }))}
                                filterOption={(input, opt) =>
                                    opt.label.toLowerCase().includes(input.toLowerCase())}
                            />
                        </Form.Item>
                    </CustomCard>
                  </Space>
                </Form>

                <Flex gap={16} wrap>
                    {stats.map((st) => (
                        <div key={st.title} style={{ flex: '1 1 190px', minWidth: 190 }}>
                            <CustomCard>
                                <Statistic {...st} />
                            </CustomCard>
                        </div>
                    ))}
                </Flex>

                <CustomCard
                    title="What customers will see"
                    extra={mode && (
                        <Tag color={mode === 'telna' ? 'blue' : 'default'}>
                            {mode === 'telna'
                                ? 'Per-megabyte · Telna'
                                : 'Fixed bundles · eSIM Access'}
                        </Tag>
                    )}
                >
                    <Paragraph type="secondary" style={{ marginTop: 0 }}>
                        Read from the same endpoint the app calls, so this cannot drift from
                        what a phone is actually served. Save first to see a change here.
                    </Paragraph>
                    {mode !== 'telna' && (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="The provider switch is on eSIM Access"
                            description={
                                'These destinations are fixed bundles at the provider\'s own '
                                + 'prices, so the markup and the per-megabyte limits above do '
                                + 'not apply to them. Change the provider in Settings to sell '
                                + 'per megabyte.'
                            }
                        />
                    )}
                    <Table
                        rowKey="iso2"
                        dataSource={preview}
                        columns={columns}
                        size="small"
                        scroll={{ x: 'max-content' }}
                        pagination={{ pageSize: 15, showSizeChanger: false }}
                        locale={{
                            emptyText: settings
                                ? 'No destinations. Check the countries list above.'
                                : 'Loading…',
                        }}
                    />
                </CustomCard>

                {settings && (
                    <Text type="secondary">
                        Slider range {data(settings.minMb)} – {data(settings.maxMb)} in
                        {' '}{settings.stepMb} MB steps.
                    </Text>
                )}
            </Space>
        </Spin>
    );
}
